import { useContext } from 'react';

import deepCompare from 'react-fast-compare';

import {
  type PostType,
  type QueryParams,
  getQueryKey,
  loadCount,
  loadPosts,
} from 'common/actions/queueItemQueries';
import { LocationContext } from 'common/containers/RouterContainer';
import asyncConnect from 'common/core/asyncConnect';
import cloneElementWithProps from 'common/core/cloneElementWithProps';
import { isNotNil } from 'common/util/isNil';
import mapify from 'common/util/mapify';

import { CompanyContext } from './CompanyContainer';

import type { Board } from 'common/api/endpoints/boards';
import type { State } from 'common/reducers/queueItemQueries';
import type { AsyncItem } from 'redux-connect';

type EncodedQuery = {
  boardURLNames?: string;
  dateRange?: string;
  sort?: 'created' | 'source' | 'title';
  sortDirection?: 'asc' | 'desc';
  postTypes?: string;
};

type LocationWithQuery = {
  pathname: string;
  query: EncodedQuery;
};

type Props = {
  boards: Board[];
  children: React.ReactNode;
  queueItemQueries: State;
};

export const asyncLoadCount: AsyncItem = {
  promise: ({ store: { dispatch } }) => {
    return dispatch(loadCount());
  },
};

export const getQueryParams = (location: LocationWithQuery): QueryParams => {
  const {
    query: { boardURLNames, dateRange, sort, sortDirection, postTypes },
  } = location;

  return {
    ...(boardURLNames && { boardURLNames: boardURLNames.split('_') }),
    ...(dateRange && { dateRange: [dateRange, dateRange] }),
    ...(sort && { sort }),
    ...(sortDirection && { sortDirection }),
    ...(postTypes && { postTypes: postTypes.split('_') as PostType[] }),
    type: 'posts',
  };
};

export const getEncodedQueryParams = (queryParams: QueryParams): EncodedQuery => {
  return {
    ...(queryParams.boardURLNames && { boardURLNames: queryParams.boardURLNames.join('_') }),
    ...(queryParams.dateRange && { dateRange: '' }), // TODO: update when adding date range filters
    ...(queryParams.sort && { sort: queryParams.sort }),
    ...(queryParams.sortDirection && { sortDirection: queryParams.sortDirection }),
    ...(queryParams.postTypes && { postTypes: queryParams.postTypes.join('_') }),
  };
};

const QueuePostItemListContainer = (props: Props) => {
  const { boards, queueItemQueries } = props;

  const location = useContext(LocationContext);
  const company = useContext(CompanyContext);

  const getQueueItemList = () => {
    const queryParams = getQueryParams(location);
    const queueQuery = queueItemQueries.queries[getQueryKey(queryParams)];

    const filterQueryParams = { ...queryParams };
    delete filterQueryParams.sort;
    delete filterQueryParams.sortDirection;

    if (!queueQuery?.itemIDs) {
      return Object.assign({}, queueQuery, { loading: true, items: [] });
    } else if (
      company?.viewerPreferences?.queuePostFilters &&
      !deepCompare(filterQueryParams, company.viewerPreferences.queuePostFilters)
    ) {
      return Object.assign({}, queueQuery, { loading: true, items: [] });
    }

    const boardMap = mapify(boards, '_id');
    return {
      ...queueQuery,
      items: queueQuery.itemIDs
        .map((itemID) => {
          const item = queueItemQueries.items[itemID];
          if (!item) {
            return null;
          }

          // add boards
          return {
            ...item,
            ...(item.type === 'post' && { board: boardMap[item.boardID] }),
            ...(item.duplicatePost && {
              duplicatePost: {
                ...item.duplicatePost,
                board: boardMap[item.duplicatePost.boardID],
              },
            }),
          };
        })
        .filter(isNotNil),
    };
  };

  const getTotalItemCount = () => {
    return queueItemQueries.postsCount;
  };

  return cloneElementWithProps(props.children, {
    ...props,
    queuePostItemList: getQueueItemList(),
    queuePostItemListTotalCount: getTotalItemCount(),
  });
};

export default asyncConnect(
  [
    {
      promise: ({ store: { dispatch }, location }) => {
        const isInbox = location.pathname.includes('/admin/inbox/canny-posts');
        if (!isInbox) {
          return;
        }

        const queryParams = isInbox ? getQueryParams(location) : ({ type: 'posts' } as QueryParams);

        return dispatch(loadPosts(queryParams));
      },
    },
  ],
  (state) => ({
    queueItemQueries: state.queueItemQueries,
  })
)(QueuePostItemListContainer);
